if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      connectedCallback() {
        this.setNodeSelectors();
        this.setDefaultOptionSets();
        this.setEventListeners();
      }

      /**
       * Sets the default option sets state value.
       */
      setDefaultOptionSets() {
        this.optionSets = this.getDefaultOptionSets();
      }

      /**
       * Sets the value of a specific option set.
       * @param {String} name - The option set name.
       * @param {Object} value - The option set state.
       */
      setOptionSet(name, state) {
        this.optionSets[name] = state;

        this.handleOptionValueState();
        this.handleOptionSetToggleState();
      }

      /**
       * Returns the default state for option sets.
       * @returns {Object}
       */
      getDefaultOptionSets() {
        const configElement = this.querySelector('[js-product-form="optionSetConfig"]');
        const config = configElement && Object.freeze(JSON.parse(configElement.innerHTML));

        if (!config) {
          return {};
        }

        return config.option_sets.reduce((sets, set) => {
          sets[set.name] = {
            type: set.default_type,
            key: set.default_key,
            value: set.default_value,
            label: set.default_label,
            title: set.default_title,
          };

          return sets;
        }, {});
      }

      /**
       * Sets the node selectors.
       */
      setNodeSelectors() {
        this.nodeSelectors = {
          data: this.querySelector('[js-product-form="data"]'),
          form: this.querySelector('[js-product-form="form"]'),
          message: this.querySelector('[js-product-form="message"]'),
          submit: this.querySelector('[js-product-form="submit"]'),
          variant: this.querySelector('[name=id]'),
          quantity: this.querySelector('[js-product-form="quantity"]'),
          optionSetConfig: this.querySelector('[js-product-form="optionSetConfig"]'),
          textOptionInputs: this.querySelectorAll('[js-product-form="textOptionInput"]'),
        };
      }

      /**
       * Returns the product data, if exists.
       * @returns {Object}
       */
      getProductData() {
        if (!this.nodeSelectors.data) {
          return;
        }

        return Object.freeze(JSON.parse(this.nodeSelectors.data.innerHTML));
      }

      /**
       * Returns the selected variant's identifier.
       * @returns {String}
       */
      getSelectedVariantId() {
        if (!this.nodeSelectors.variant) {
          return;
        }

        return Number(this.nodeSelectors.variant.value);
      }

      /**
       * Returns the selected variant object, if available.
       * @returns {Object|false}
       */
      getSelectedVariant() {
        const product = this.getProductData();

        if (!product) {
          return false;
        }

        return product.variants.find((variant) => {
          return variant.id === this.getSelectedVariantId();
        });
      }

      /**
       * Sets the event listeners.
       */
      setEventListeners() {
        if (this.nodeSelectors.form) {
          this.nodeSelectors.form.addEventListener('submit', (event) => {
            this.handleFormSubmit(event);
          });
        }

        if (this.nodeSelectors.variant) {
          this.nodeSelectors.variant.addEventListener('change', () => {
            this.handleVariantChange();
          });
        }

        [...this.nodeSelectors.textOptionInputs].forEach((input) => {
          input.addEventListener('keydown', (event) => {
            if (!input.pattern) {
              return;
            }

            /**
             * Prevents characters that don't match the pattern from being input.
             */
            if (!new RegExp(input.pattern).test(event.key)) {
              event.preventDefault();
            }
          });
        });

        this.setOptionEventListeners();
      }

      /**
       * Returns the option elements.
       * @returns {HTMLElement[]}
       */
      getOptionElements() {
        return [...this.querySelectorAll('[js-product-form="option"]')];
      }

      /**
       * Returns the option value button elements.
       * @returns {HTMLElement[]}
       */
      getOptionValueButtonElements() {
        return [...this.querySelectorAll('[js-product-form="optionValueButton"]')];
      }

      /**
       * Returns the option set toggle elements.
       * @returns {HTMLElement[]}
       */
      getOptionSetToggleElements() {
        return [...this.querySelectorAll('[js-product-form="optionSetToggle"]')];
      }

      /**
       * Sets the option event listeners.
       */
      setOptionEventListeners() {
        /**
         * Change events for option inputs (such as `select`).
         */
        this.getOptionElements().forEach((element) => {
          element.addEventListener('change', () => {
            this.handleOptionChange();
          });
        });

        /**
         * Click events for option value buttons.
         * - These act as "toggles" for specific option values, updating the underlying input.
         */
        this.getOptionValueButtonElements().forEach((element) => {
          const drawer = element.closest('toggle-element');

          element.addEventListener('click', () => {
            if (element.dataset.type === 'property') {
              this.setOptionSet(element.dataset.name, {
                type: 'property',
                key: element.dataset.key,
                value: element.dataset.value,
                label: element.dataset.label,
              });
            } else if (element.dataset.type === 'variant') {
              this.setOptionSet(element.dataset.name, {
                type: 'variant',
                value: element.dataset.value,
                label: element.dataset.label,
                title: element.dataset.title,
                togglePrice: element.dataset.togglePrice,
              });
            } else if (element.dataset.type === 'option') {
              this.getOptionElements().forEach((optionElement) => {
                if (optionElement.name === element.dataset.name) {
                  optionElement.value = element.dataset.value;
                  optionElement.dispatchEvent(new Event('change'));
                }
              });
            }

            if (drawer) {
              drawer.close();
            }
          });
        });
      }

      /**
       * Handles the option value selected state.
       */
      handleOptionValueState() {
        this.getOptionValueButtonElements().forEach((element) => {
          const type = element.dataset.type;
          const set = this.optionSets[element.dataset.name];
          let selected = false;

          if (type === 'property') {
            selected = set && set.key === element.dataset.key && set.value === element.dataset.value;
          } else if (type === 'variant') {
            selected = set && set.value === element.dataset.value;
          } else if (type === 'option') {
            const form = new FormData(this.nodeSelectors.form);

            selected = form.has(element.dataset.name) && form.get(element.dataset.name) === element.dataset.value;
          }

          if (element.hasAttribute('data-unavailable')) {
            element.disabled = true;
            element.innerHTML = theme.strings.sections.main_product.out_of_stock;
            return;
          }

          element.disabled = selected;
          element.innerHTML = theme.strings.sections.main_product[
            selected ? 'option_drawer_selected' : 'option_drawer_choose_value_html'
          ].replace('{{ price }}', element.dataset.price);
        });
      }

      /**
       * Handles the selected state for the option set toggle buttons.
       * - Displays the currently selected value as the button text.
       */
      handleOptionSetToggleState() {
        this.getOptionSetToggleElements().forEach((element) => {
          const set = this.optionSets[element.dataset.optionSet];

          if (!set) {
            return;
          }

          element.innerHTML = `${set.label}${
            set.togglePrice ? ` <span class="text-weight-medium">${set.togglePrice}</span>` : ''
          }`;
        });
      }

      /**
       * Sets the add to cart state.
       * @param {String} state - The state to set.
       */
      setAddToCartState(state = 'idle') {
        if (!state || !this.nodeSelectors.submit) {
          return;
        }

        switch (state) {
          case 'idle':
            this.nodeSelectors.submit.innerHTML = theme.strings.sections.main_product.add_to_cart;
            this.nodeSelectors.submit.disabled = false;
            break;

          case 'adding':
            this.nodeSelectors.submit.innerHTML = theme.strings.sections.main_product.adding_to_cart;
            this.nodeSelectors.submit.disabled = true;
            break;

          case 'no_stock':
            this.nodeSelectors.submit.innerHTML = theme.strings.sections.main_product.out_of_stock;
            this.nodeSelectors.submit.disabled = true;
            break;

          case 'unavailable':
            this.nodeSelectors.submit.innerHTML = theme.strings.sections.main_product.unavailable;
            this.nodeSelectors.submit.disabled = true;
            break;
        }
      }

      /**
       * Sets the form message state.
       * @param {String} message - The message to set.
       */
      setMessage(message = '') {
        if (!this.nodeSelectors.message) {
          return;
        }

        this.nodeSelectors.message.innerHTML = message;
      }

      /**
       * Handles the form submit event.
       */
      async handleFormSubmit(event) {
        const form = new FormData(event.target);
        const addOnItems = [];

        event.preventDefault();

        this.setAddToCartState('adding');
        this.setMessage();

        Object.keys(this.optionSets).forEach((key) => {
          const set = this.optionSets[key];

          if (set.type === 'property') {
            form.append(`properties[${set.key}]`, set.value);
          } else if (set.type === 'variant') {
            addOnItems.push(set);

            /**
             * Set the admin-friendly add-on label.
             */
            form.append(`properties[_${set.title}]`, set.label);
          }
        });

        if (addOnItems.length) {
          const key = new Date().getTime().toString();

          form.append('properties[_key]', key);

          await fetch(`${theme.routes.cart_add_url}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: addOnItems.map((addOnSet) => ({
                id: Number(addOnSet.value),
                quantity: 1,
                properties: {
                  _add_on_parent: key,
                  _property_key: addOnSet.key,
                },
              })),
            }),
          });
        }

        fetch(theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
          },
          body: form,
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.description) {
              this.setMessage(response.description);
            }

            theme.eventBus.emit('cart:update');
            theme.eventBus.emit('cart:added');

            this.nodeSelectors.quantity.value = 1;
          })
          .finally(() => {
            this.setAddToCartState();
          })
          .catch(() => {
            this.nodeSelectors.form.submit();
          });
      }

      /**
       * Handles the option change event.
       */
      handleOptionChange() {
        const selectedVariant = this.getVariantBySelectedOptions();

        if (!selectedVariant) {
          this.setAddToCartState('unavailable');
          return;
        }

        this.nodeSelectors.variant.value = selectedVariant.id;

        this.nodeSelectors.variant.dispatchEvent(new Event('change'));
      }

      /**
       * Handles the variant change event.
       * - Sets the form state based on the selected variant.
       */
      handleVariantChange() {
        const selected = this.getSelectedVariant();

        if (!selected) {
          return;
        }

        this.dispatchEvent(
          new CustomEvent('variant', {
            detail: selected,
          })
        );

        if (!selected.available) {
          this.setAddToCartState('no_stock');
          return;
        }

        this.setAddToCartState();
      }

      /**
       * Returns the selected option values.
       * @returns {Object}
       */
      getSelectedOptions() {
        const selected = {};

        this.getOptionElements().forEach((element) => {
          const name = element.name;

          if (element.tagName === 'SELECT' || element.checked) {
            selected[name] = element.value;
          }
        });

        return selected;
      }

      /**
       * Returns the variant object by its selected options.
       * @returns {Object}
       */
      getVariantBySelectedOptions() {
        const selectedOptions = this.getSelectedOptions();
        const product = this.getProductData();

        return product.variants.find((variant) => {
          return Object.keys(selectedOptions).every((key, index) => variant.options[index] === selectedOptions[key]);
        });
      }
    }
  );
}
